import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  UserOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import "../../../config";

const { Text } = Typography;

const ReactivationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        global.config.APIURL.BaseURL + "api/ReactivationAdmin/GetPendingRequests",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(res.data || []);
    } catch {
      message.error("Failed to load reactivation requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (requestId) => {
    setApprovingId(requestId);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        global.config.APIURL.BaseURL + `api/ReactivationAdmin/ApproveRequest?requestId=${requestId}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Account reactivated successfully.");
      setRequests((prev) => prev.filter((r) => r.RequestID !== requestId));
    } catch {
      message.error("Failed to approve request.");
    } finally {
      setApprovingId(null);
    }
  };

  const columns = [
    {
      title: "Customer",
      key: "customer",
      render: (_, r) => (
        <Space size={8}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "#f0f5ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UserOutlined style={{ color: "#2f54eb", fontSize: 14 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#141414" }}>
              {r.ContactPersonName || "—"}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.FirmName || "—"}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Mobile",
      dataIndex: "MobileNo",
      key: "MobileNo",
      width: 140,
      render: (v) =>
        v ? (
          <Space size={4}>
            <PhoneOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
            <Text style={{ fontSize: 13 }}>{v}</Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Requested On",
      dataIndex: "RequestedOn",
      key: "RequestedOn",
      width: 160,
      render: (v) => (
        <Text style={{ fontSize: 13 }}>
          {v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "—"}
        </Text>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: () => (
        <Tag color="warning" style={{ fontSize: 11 }}>
          Pending
        </Tag>
      ),
    },
    {
      title: "",
      key: "action",
      width: 120,
      align: "center",
      render: (_, r) => (
        <Popconfirm
          title="Reactivate this account?"
          description={`This will restore login access for ${r.ContactPersonName || "this user"}.`}
          onConfirm={() => handleApprove(r.RequestID)}
          okText="Yes, Reactivate"
          cancelText="Cancel"
          okButtonProps={{ type: "primary" }}
        >
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            loading={approvingId === r.RequestID}
            style={{ borderRadius: 6 }}
          >
            Approve
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        padding: "20px 24px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#141414" }}>
            Reactivation Requests
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {loading
              ? "Loading…"
              : requests.length === 0
              ? "No pending requests"
              : `${requests.length} pending request${requests.length > 1 ? "s" : ""}`}
          </Text>
        </div>
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={fetchRequests} loading={loading} />
        </Tooltip>
      </div>

      <Table
        dataSource={requests}
        columns={columns}
        rowKey="RequestID"
        loading={loading}
        size="middle"
        scroll={{ x: 600 }}
        pagination={{ pageSize: 20, size: "small", showSizeChanger: false }}
        locale={{ emptyText: "No pending reactivation requests" }}
        rowClassName={() => "reactivation-row"}
      />

      <style>{`
        .reactivation-row:hover > td {
          background: #f6f8ff !important;
        }
        .ant-table-thead > tr > th {
          background: #fafafa !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #8c8c8c !important;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 1px solid #f0f0f0 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f5f5f5 !important;
          padding: 12px 16px !important;
        }
        .ant-table {
          border-radius: 8px !important;
          border: 1px solid #f0f0f0 !important;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ReactivationRequests;
